// ─── GROUPING KEYS (still needed for concatenation logic) ───────────────────

// Fields EXCLUDED from grouping key for instruments → concatenated instead
export const INSTRUMENT_CONCAT_KEYS = new Set([
  "PandID",
  "PandIDNumber",
  "P&ID Number",
  "Line Number",
  "LineNumber",
  "Spec",
  "Tag Number",
  "TagNumber",
  "Loop Number",
  "LoopNumber",
  "Hook-up Number",
  "HookUpNumber",
  "HookupNumber",
]);

// Fields EXCLUDED from grouping key for valves → concatenated instead
export const VALVE_CONCAT_KEYS = new Set([
  "TagNumber",
  "Tag",
  "tagNumber",
  "PandIDNumber",
  "PandID",
  "pandIDNumber",
  "pandid",
  "LineNumber",
  "Line",
  "lineNumber",
  "Notes",
  "notes",
]);

// Fields EXCLUDED from grouping key for equipment → concatenated instead.
// Equipment is typically unique per tag, so this set is small. Tag-like and
// drawing-origin fields still get concatenated for safety when the same piece
// of equipment appears on multiple drawings.
export const EQUIPMENT_CONCAT_KEYS = new Set([
  "Tag Number",
  "TagNumber",
  "Tag",
  "tagNumber",
  "PandID",
  "PandIDNumber",
  "P&ID Number",
  "Notes",
  "notes",
]);

// ─── HELPERS ────────────────────────────────────────────────────────────────

export function stripQcad(key) {
  return String(key ?? "")
    .replace(/^QCAD:/i, "")
    .trim();
}

export function normalizeDataSheetData(raw) {
  if (!raw || typeof raw !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    out[stripQcad(k)] = v;
  }
  return out;
}

function norm(val) {
  return String(val ?? "").trim();
}

export function isValidRow(row) {
  if (!row || typeof row !== "object") return false;
  return Object.values(row).some((v) => String(v ?? "").trim().length > 0);
}

// ─── DYNAMIC TEMPLATE SUPPORT ────────────────────────────────────────────────

/**
 * Look up a value from a data row supporting multiple key format variants.
 * Tries: exact → camelCase → PascalCase → no-spaces → case-insensitive.
 * Does NOT do partial matching.
 */
function getValueFromRow(row, propertyName) {
  if (!row || !propertyName) return "";

  const searchTerm = propertyName.trim();

  // Direct exact match
  if (
    row[searchTerm] !== undefined &&
    row[searchTerm] !== null &&
    row[searchTerm] !== ""
  ) {
    return norm(row[searchTerm]);
  }

  // Create variations of the search term
  const variations = new Set();

  // Remove spaces: "Project Name" → "ProjectName"
  const noSpaces = searchTerm.replace(/[\s_\-]+/g, "");
  variations.add(noSpaces);

  // camelCase: "Project Name" → "projectName"
  const camelCase = noSpaces.charAt(0).toLowerCase() + noSpaces.slice(1);
  variations.add(camelCase);

  // PascalCase (same as noSpaces but ensure first letter uppercase)
  const pascalCase = noSpaces.charAt(0).toUpperCase() + noSpaces.slice(1);
  variations.add(pascalCase);

  // lowercase: "Project Name" → "projectname"
  variations.add(noSpaces.toLowerCase());

  // With underscores: "Project Name" → "Project_Name"
  variations.add(searchTerm.replace(/[\s]+/g, "_"));

  // Lowercase with spaces
  variations.add(searchTerm.toLowerCase());

  // Check all variations against row keys (exact match only)
  for (const variation of variations) {
    if (
      row[variation] !== undefined &&
      row[variation] !== null &&
      row[variation] !== ""
    ) {
      return norm(row[variation]);
    }
  }

  // Case-insensitive exact match (key must match exactly except for case)
  const lowerSearch = searchTerm.toLowerCase();
  for (const [key, value] of Object.entries(row)) {
    if (value === undefined || value === null || value === "") continue;
    if (key.toLowerCase() === lowerSearch) {
      return norm(value);
    }
  }

  // No match found
  return "";
}

function findTableByName(allTables, templateName) {
  if (!allTables || !templateName) return null;
  const template = allTables[templateName];
  if (template) return template;
  const matchingKey = Object.keys(allTables).find(
    (key) => key.toLowerCase() === templateName.toLowerCase(),
  );
  return matchingKey ? allTables[matchingKey] : null;
}

export function isEquipmentRow(row) {
  if (!row || typeof row !== "object") return false;
  const category = norm(row?.Category ?? row?.category);
  const type = norm(row?.Type ?? row?.type);
  return (
    category.toLowerCase() === "equipment" || type.toLowerCase() === "equipment"
  );
}

export function getEquipmentType(row) {
  if (!row || typeof row !== "object") return "";

  const direct =
    norm(row?.["Equipment Type"]) ||
    norm(row?.EquipmentType) ||
    norm(row?.equipmentType);
  if (direct) return direct;

  // Fallback: try Component / Sub-category style fields
  const fallback =
    norm(row?.Component) ||
    norm(row?.component) ||
    norm(row?.SubCategory) ||
    norm(row?.["Sub Category"]);
  return fallback;
}

/**
 * Resolve the template rows for a given component / tabKey.
 *
 * Valves:       DS_{ComponentName}_Template        (spaces → underscores)
 * Instruments:  DS_Instruments_Template            (shared for all instruments)
 * Equipment:    DS_Equipment_{EquipmentType}_Template
 *               (EquipmentType is "Rotating" or "Stationary", normalized for
 *                case and spacing)
 *
 * For equipment, `componentName` is re-used as the equipment-type argument so
 * existing callers that pass the row's component name still work.
 *
 * Returns array of { PropertyGroup, Property, Value } rows, or null.
 */
export function getTemplateForComponent(componentName, allTables, tabKey) {
  if (!allTables || typeof allTables !== "object") return null;

  if (tabKey === "instrument") {
    return allTables["DS_Instruments_Template"] ?? null;
  }

  if (tabKey === "valve" && componentName) {
    const normalizedComponent = componentName
      .replace(/[\s_]+/g, "_")
      .replace(/^_+|_+$/g, "");
    const templateName = `DS_${normalizedComponent}_Template`;
    return findTableByName(allTables, templateName);
  }

  if (tabKey === "equipment") {
    const equipmentType = String(componentName ?? "").trim();
    if (equipmentType) {
      const normalizedType = equipmentType
        .replace(/[\s_]+/g, "_")
        .replace(/^_+|_+$/g, "");
      const lower = normalizedType.toLowerCase();

      // Canonicalize common variants so "rotating", "ROTATING", etc. all land
      // on the same template.
      let canonical = normalizedType;
      if (lower.includes("rotating")) canonical = "Rotating";
      else if (lower.includes("stationary")) canonical = "Stationary";

      const templateName = `DS_Equipment_${canonical}_Template`;
      const direct = findTableByName(allTables, templateName);
      if (direct) return direct;
    }

    // No specific match — fall back to any DS_Equipment_*_Template so equipment
    // still gets a data sheet if the type couldn't be inferred.
    const fallbackKey = Object.keys(allTables).find((key) =>
      /^DS_Equipment_.+_Template$/i.test(key),
    );
    return fallbackKey ? allTables[fallbackKey] : null;
  }

  return null;
}

/**
 * Build categorized fields from a GROUP of rows using a template schema.
 *
 * templateRows : array of { PropertyGroup, Property, Value }
 * rows         : array of actual data rows (same-component group)
 * concatKeys   : Set of property names whose values should be concatenated
 *
 * Returns { [sectionTitle]: { [fieldLabel]: value } } or null if no template.
 */
export function buildCategorizedFieldsFromTemplate(
  rows,
  templateRows,
  concatKeys = new Set(),
) {
  if (!templateRows || templateRows.length === 0) {
    return null;
  }

  const primaryRow = rows[0];

  // Cache concatenated values so we don't recompute per-field
  const concatCache = {};
  const getConcatValue = (property) => {
    if (concatCache[property] !== undefined) return concatCache[property];
    const values = new Set();
    for (const row of rows) {
      const val = getValueFromRow(row, property);
      if (val) values.add(val);
    }
    concatCache[property] = [...values].join(", ");
    return concatCache[property];
  };

  // Build sections preserving template order (based on PropertyGroup from template)
  const sections = {};
  const sectionOrder = [];

  for (const templateRow of templateRows) {
    const group = norm(templateRow.PropertyGroup);
    const property = norm(templateRow.Property);

    // Skip if missing critical fields
    if (!group || !property) continue;

    if (!sections[group]) {
      sections[group] = {};
      sectionOrder.push(group);
    }

    const value = concatKeys.has(property)
      ? getConcatValue(property)
      : getValueFromRow(primaryRow, property);

    // Store value - even if empty, we want to show the field
    sections[group][property] = value || "";
  }

  // Return ALL sections and ALL fields, even if empty
  const result = {};
  for (const title of sectionOrder) {
    result[title] = sections[title];
  }

  return result;
}

// ─── INSTRUMENT GROUPING ────────────────────────────────────────────────────

function buildInstrumentGroupKey(row) {
  return Object.entries(row)
    .filter(([k]) => !INSTRUMENT_CONCAT_KEYS.has(k))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${norm(v)}`)
    .join("||");
}

function buildValveGroupKey(row) {
  return Object.entries(row)
    .filter(([k]) => {
      if (VALVE_CONCAT_KEYS.has(k)) return false;
      const lower = k.toLowerCase();
      if (
        lower.includes("path") ||
        lower === "id" ||
        lower === "select" ||
        lower === "actions"
      )
        return false;
      return true;
    })
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${norm(v)}`)
    .join("||");
}

// ─── VALVE CATEGORIZED FIELDS ────────────────────────────────────────────────

// Build categorized fields from a GROUP of valve rows.
// Uses dynamic template from allTables. If no template exists, returns null.
function buildValveCategorizedFieldsFromRows(rows, allTables = {}) {
  if (!rows || rows.length === 0) return null;

  const primaryRow = rows[0];

  // Get component name from multiple possible field names
  const componentName =
    norm(primaryRow?.Component) ||
    norm(primaryRow?.component) ||
    norm(primaryRow?.Components) ||
    norm(primaryRow?.components) ||
    norm(primaryRow?.["Valve Type"]) ||
    norm(primaryRow?.["ValveType"]) ||
    "";

  // Try template-driven rendering first
  const templateRows = getTemplateForComponent(
    componentName,
    allTables,
    "valve",
  );

  if (templateRows && templateRows.length > 0) {
    return buildCategorizedFieldsFromTemplate(
      rows,
      templateRows,
      VALVE_CONCAT_KEYS,
    );
  }

  // No template found - return null
  return null;
}

// Build categorized fields from a GROUP of instrument rows.
// Uses DS_Instruments_Template. If no template exists, returns null.
function buildInstrumentCategorizedFieldsFromRows(rows, allTables = {}) {
  if (!rows || rows.length === 0) return null;

  // Try template-driven rendering first
  const templateRows = getTemplateForComponent(null, allTables, "instrument");

  if (templateRows && templateRows.length > 0) {
    return buildCategorizedFieldsFromTemplate(
      rows,
      templateRows,
      INSTRUMENT_CONCAT_KEYS,
    );
  }

  // No template found - return null
  return null;
}

// Build categorized fields from a GROUP of equipment rows using
// DS_Equipment_{Rotating|Stationary}_Template. Returns null when no template
// is available for the row's equipment type.
function buildEquipmentCategorizedFieldsFromRows(rows, allTables = {}) {
  if (!rows || rows.length === 0) return null;

  const primaryRow = rows[0];
  const equipmentType = getEquipmentType(primaryRow);

  const templateRows = getTemplateForComponent(
    equipmentType,
    allTables,
    "equipment",
  );

  if (templateRows && templateRows.length > 0) {
    return buildCategorizedFieldsFromTemplate(
      rows,
      templateRows,
      EQUIPMENT_CONCAT_KEYS,
    );
  }

  return null;
}

function buildEquipmentGroupKey(row) {
  return Object.entries(row)
    .filter(([k]) => {
      if (EQUIPMENT_CONCAT_KEYS.has(k)) return false;
      const lower = k.toLowerCase();
      if (
        lower.includes("path") ||
        lower === "id" ||
        lower === "select" ||
        lower === "actions" ||
        lower === "datasheet"
      )
        return false;
      return true;
    })
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${norm(v)}`)
    .join("||");
}

// ─── MAIN CONSOLIDATION ──────────────────────────────────────────────────────

export function consolidateDataSheets(sourceRows, tabKey, allTables = {}) {
  if (!sourceRows || sourceRows.length === 0) {
    return {
      tab: tabKey,
      totalSourceRows: 0,
      totalGroupedSheets: 0,
      items: [],
    };
  }

  if (tabKey === "valve") {
    const groups = new Map();
    sourceRows.forEach((row) => {
      const key = buildValveGroupKey(row);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });

    const items = [...groups.values()].map((rows) => {
      const categorizedFields = buildValveCategorizedFieldsFromRows(
        rows,
        allTables,
      );

      // Collect tag numbers from multiple possible field names
      const tagNumbers = [
        ...new Set(
          rows
            .map(
              (r) =>
                norm(r["Tag Number"]) ||
                norm(r.TagNumber) ||
                norm(r.tagNumber) ||
                norm(r.Tag) ||
                norm(r.tag),
            )
            .filter(Boolean),
        ),
      ];

      return {
        categorizedFields,
        tagNumbers,
        count: rows.length,
        rawData: rows,
      };
    });

    return {
      tab: "valve",
      totalSourceRows: sourceRows.length,
      totalGroupedSheets: items.length,
      items,
    };
  }

  if (tabKey === "instrument") {
    const groups = new Map();
    sourceRows.forEach((row) => {
      const key = buildInstrumentGroupKey(row);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });

    const items = [...groups.values()].map((rows) => {
      const categorizedFields = buildInstrumentCategorizedFieldsFromRows(
        rows,
        allTables,
      );

      const tagNumbers = [
        ...new Set(
          rows
            .map(
              (r) =>
                norm(r["Tag Number"]) || norm(r.TagNumber) || norm(r.tagNumber),
            )
            .filter(Boolean),
        ),
      ];

      return {
        categorizedFields,
        tagNumbers,
        count: rows.length,
        rawData: rows,
      };
    });

    return {
      tab: "instrument",
      totalSourceRows: sourceRows.length,
      totalGroupedSheets: items.length,
      items,
    };
  }

  if (tabKey === "equipment") {
    // Group by tag number first (equipment is typically unique per tag); fall
    // back to a full-row grouping key for rows missing a tag.
    const groups = new Map();
    sourceRows.forEach((row) => {
      const tag = getRowTagNumber(row);
      const key = tag ? `tag:${tag}` : `row:${buildEquipmentGroupKey(row)}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });

    const items = [...groups.values()].map((rows) => {
      const categorizedFields = buildEquipmentCategorizedFieldsFromRows(
        rows,
        allTables,
      );

      const tagNumbers = [
        ...new Set(rows.map(getRowTagNumber).filter(Boolean)),
      ];

      return {
        categorizedFields,
        tagNumbers,
        count: rows.length,
        rawData: rows,
      };
    });

    return {
      tab: "equipment",
      totalSourceRows: sourceRows.length,
      totalGroupedSheets: items.length,
      items,
    };
  }

  return { tab: tabKey, totalSourceRows: 0, totalGroupedSheets: 0, items: [] };
}

export function getRowTagNumber(row) {
  return norm(row?.["Tag Number"] ?? row?.TagNumber ?? row?.tagNumber);
}

// ─── PER-ROW DATA SHEET (used by Trim List) ─────────────────────────────────
//
// Given a single trim-list row, plus the Valve and Instrument source tables
// and allTables (for templates), return a categorized data sheet object of
// the form { [sectionTitle]: { [fieldLabel]: value } }, or null if the row
// has no matching source rows / no template available.
//
// Matching strategy: locate all valve/instrument rows sharing the trim row's
// Tag Number. If none match by tag, fall back to Spec+Component+Size match
// against the valve table. Whichever side (valve/instrument) yields matches
// determines which template is used.
// Build a data sheet directly from a single valve or instrument row.
// Picks the template based on `tabKey` ("valve" or "instrument") and the row's
// component name. Returns a categorizedFields object or null if no template.
export function buildDataSheetForSourceRow(row, tabKey, allTables = {}) {
  if (!row) return null;

  if (tabKey === "instrument") {
    const templateRows = getTemplateForComponent(null, allTables, "instrument");
    if (!templateRows || templateRows.length === 0) return null;
    return buildCategorizedFieldsFromTemplate(
      [row],
      templateRows,
      INSTRUMENT_CONCAT_KEYS,
    );
  }

  if (tabKey === "valve") {
    const componentName =
      norm(row?.Component) ||
      norm(row?.component) ||
      norm(row?.Components) ||
      norm(row?.components) ||
      norm(row?.["Valve Type"]) ||
      norm(row?.ValveType) ||
      "";
    const templateRows = getTemplateForComponent(componentName, allTables, "valve");
    if (!templateRows || templateRows.length === 0) return null;
    return buildCategorizedFieldsFromTemplate(
      [row],
      templateRows,
      VALVE_CONCAT_KEYS,
    );
  }

  if (tabKey === "equipment") {
    const equipmentType = getEquipmentType(row);
    const templateRows = getTemplateForComponent(
      equipmentType,
      allTables,
      "equipment",
    );
    if (!templateRows || templateRows.length === 0) return null;
    return buildCategorizedFieldsFromTemplate(
      [row],
      templateRows,
      EQUIPMENT_CONCAT_KEYS,
    );
  }

  return null;
}

export function buildRowDataSheet(
  trimRow,
  valveRows,
  instrumentRows,
  allTables = {},
  equipmentRows = [],
) {
  // Equipment rows get their own template based on Equipment Type, independent
  // of valve/instrument matching. Detect them up front.
  if (isEquipmentRow(trimRow)) {
    const equipment = Array.isArray(equipmentRows) ? equipmentRows : [];
    const tag = getRowTagNumber(trimRow);

    const matchedEquipment = tag
      ? equipment.filter((r) => getRowTagNumber(r) === tag)
      : [];

    const rowsForTemplate =
      matchedEquipment.length > 0 ? matchedEquipment : [trimRow];

    const equipmentType = getEquipmentType(rowsForTemplate[0]);
    const templateRows = getTemplateForComponent(
      equipmentType,
      allTables,
      "equipment",
    );

    if (!templateRows || templateRows.length === 0) return null;

    return buildCategorizedFieldsFromTemplate(
      rowsForTemplate,
      templateRows,
      EQUIPMENT_CONCAT_KEYS,
    );
  }

  const tag = getRowTagNumber(trimRow);
  const spec = norm(trimRow?.Spec ?? trimRow?.spec);
  const component = norm(
    trimRow?.Component ??
      trimRow?.Components ??
      trimRow?.component ??
      trimRow?.components,
  );
  const size = norm(trimRow?.Size ?? trimRow?.size);

  const valves = Array.isArray(valveRows) ? valveRows : [];
  const instruments = Array.isArray(instrumentRows) ? instrumentRows : [];

  const matchByTag = (rows) =>
    tag ? rows.filter((r) => getRowTagNumber(r) === tag) : [];

  let matchedRows = matchByTag(instruments);
  let tabKey = matchedRows.length > 0 ? "instrument" : null;

  if (!tabKey) {
    matchedRows = matchByTag(valves);
    tabKey = matchedRows.length > 0 ? "valve" : null;
  }

  if (!tabKey && spec && component) {
    const fallback = valves.filter(
      (r) =>
        norm(r?.Spec) === spec &&
        (norm(r?.Component) === component ||
          norm(r?.Components) === component) &&
        (!size || norm(r?.Size) === size),
    );
    if (fallback.length > 0) {
      matchedRows = fallback;
      tabKey = "valve";
    }
  }

  if (!tabKey || matchedRows.length === 0) return null;

  const templateRows =
    tabKey === "instrument"
      ? getTemplateForComponent(null, allTables, "instrument")
      : getTemplateForComponent(component, allTables, "valve");

  if (!templateRows || templateRows.length === 0) return null;

  const concatKeys =
    tabKey === "instrument" ? INSTRUMENT_CONCAT_KEYS : VALVE_CONCAT_KEYS;

  return buildCategorizedFieldsFromTemplate(matchedRows, templateRows, concatKeys);
}
